import { useMemo } from "react";
import {
  ApprovalCard,
  type ApprovalDecision,
  type SerializableApprovalCard,
} from "~/components/tool-ui/approval-card";
import { HitlLoadError, HitlPending } from "./HitlStatus";
import type { HitlToolProps } from "./hitlTools";
import { parseToolBody } from "./parseToolBody";
import type { ToolCallEntry } from "./useTurns";

export type ApprovalCardContent = Omit<SerializableApprovalCard, "id" | "role" | "choice">;

export interface ApprovalCardSource {
  // Null while the args stream in or if they don't parse.
  toCard: (
    args: string,
    turnToolCalls: readonly ToolCallEntry[] | undefined
  ) => ApprovalCardContent | null;
  pendingLabel: string;
  errorLabel: string;
}

// The one approve/deny card; each approval-style tool supplies only its source.
export function makeApprovalCardTool({ toCard, pendingLabel, errorLabel }: ApprovalCardSource) {
  return function ApprovalCardTool({ entry, isRunning, onAnswer, turnToolCalls }: HitlToolProps) {
    const card = useMemo(() => toCard(entry.args, turnToolCalls), [entry.args, turnToolCalls]);

    if (!card) {
      return isRunning ? (
        <HitlPending>{pendingLabel}</HitlPending>
      ) : (
        <HitlLoadError>{errorLabel}</HitlLoadError>
      );
    }

    // Any result other than a denial means the tool ran.
    const choice: ApprovalDecision | undefined =
      entry.result === undefined
        ? undefined
        : entry.denied || entry.result === "denied"
          ? "denied"
          : "approved";

    return (
      <ApprovalCard
        {...card}
        id={entry.id}
        choice={choice}
        collapsibleSections
        onConfirm={onAnswer ? () => onAnswer(entry.id, "approved") : undefined}
        onCancel={onAnswer ? () => onAnswer(entry.id, "denied") : undefined}
      />
    );
  };
}

// `ask_for_approval`'s args already are the card.
export const askForApproval: ApprovalCardSource = {
  toCard: (args) => {
    const view = parseToolBody(args);
    if (view.kind !== "json" || Array.isArray(view.value)) return null;
    const value = view.value as { title?: unknown };
    if (typeof value.title !== "string" || value.title.length === 0) return null;
    return value as ApprovalCardContent;
  },
  pendingLabel: "Preparing approval…",
  errorLabel: "Couldn't load this approval.",
};
